import MenuBook from "@mui/icons-material/MenuBook";
import { Box, ButtonBase } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useId, useState, type MouseEvent } from "react";

import { ScoutSkillDetailPopover } from "./ScoutSkillDetailPopover";
import { useScoutSkill } from "./useScoutSkillCatalog";

type ScoutSkillPillProps = {
  /** The exact skill, when known; otherwise the first visible skill with `slug`. */
  id?: string;
  slug?: string;
  /** Name from the tool result; used when the catalog can't resolve the skill. */
  name?: string;
};

/**
 * Teal book pill for a skill. Click (or Enter/Space) opens the skill's
 * details. Events are stopped so a pill inside the tool-group header doesn't
 * also toggle the group (React events bubble through the popover's portal).
 */
export function ScoutSkillPill({ id, slug, name }: ScoutSkillPillProps) {
  const skill = useScoutSkill({ id, slug });
  const shownSlug = skill?.slug ?? slug;
  const label = skill?.displayName ?? name ?? (shownSlug ? `/${shownSlug}` : "Skill");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popoverId = useId();
  const open = Boolean(anchorEl);

  return (
    <>
      <ButtonBase
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-label={`${label} skill. Show details`}
        onClick={(event: MouseEvent<HTMLElement>) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        // Keep Enter/Space on the pill from also toggling a clickable parent row.
        onKeyDown={(event) => event.stopPropagation()}
        sx={(theme: Theme) => ({
          display: "inline-flex",
          alignItems: "center",
          gap: 0.625,
          verticalAlign: "middle",
          height: 24,
          pl: 1,
          pr: 1.25,
          borderRadius: "12px",
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          color: "primary.main",
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1,
          maxWidth: "100%",
          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.14) },
          "&.Mui-focusVisible": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        })}
      >
        <MenuBook aria-hidden sx={{ fontSize: 15 }} />
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {label}
        </Box>
      </ButtonBase>
      <ScoutSkillDetailPopover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        slug={shownSlug ?? ""}
        skill={skill}
        fallbackName={name}
      />
    </>
  );
}
